import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  CopyCheck,
  Eye,
  Filter,
  Key,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@/components/common/Tooltip';
import { useAuth } from '@/hooks';
import { adminService } from '@/services';
import type { User } from '@/types';
import {
  DEPARTMENTS,
  formatEmployeeId,
  formatNaira,
  formatStatus,
  showErrorToast,
  showSuccessToast,
  TOAST_CONFIG,
} from '@/utils';
import { cn } from '@/utils/cn';

export function StaffManagement() {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [department, setDepartment] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [confirmPasswordModalOpen, setConfirmPasswordModalOpen] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [pendingSuspendUserId, setPendingSuspendUserId] = useState<string | null>(null);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [pendingOverrideUser, setPendingOverrideUser] = useState<User | null>(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [pendingApproveUserId, setPendingApproveUserId] = useState<string | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [pendingRejectUserId, setPendingRejectUserId] = useState<string | null>(null);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [pendingActivateUserId, setPendingActivateUserId] = useState<string | null>(null);

  const isCurrentSuperAdmin = useMemo(
    () => (user: User) => {
      if (!currentUser) {
        return false;
      }
      const currentUserId = String(currentUser._id || '');
      const userId = String(user._id || '');
      const isSameUser =
        currentUserId === userId ||
        (currentUser.username && user.username && currentUser.username === user.username);
      return isSameUser && currentUser.role === 'super_admin';
    },
    [currentUser],
  );

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await adminService.getStaffList({
        search: debouncedSearch || undefined,
        department,
        status,
        page,
        limit: pageSize,
      });
      const sortedUsers = [...data.users].sort((a, b) => a.employeeId - b.employeeId);
      setUsers(sortedUsers);
      setTotal(data.total);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, department, status, debouncedSearch]);

  const handleOverrideClick = (user: User) => {
    setPendingOverrideUser(user);
    setOverrideModalOpen(true);
  };

  const handleApproveClick = (userId: string) => {
    setPendingApproveUserId(userId);
    setApproveModalOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!pendingApproveUserId) return;
    setApproveModalOpen(false);
    try {
      await adminService.approveUser(pendingApproveUserId);
      fetchUsers();
      showSuccessToast('User account has been approved');
    } catch (error) {
      showErrorToast('Failed to approve user account');
    } finally {
      setPendingApproveUserId(null);
    }
  };

  const handleRejectClick = (userId: string) => {
    setPendingRejectUserId(userId);
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!pendingRejectUserId) return;
    setRejectModalOpen(false);
    try {
      await adminService.rejectUser(pendingRejectUserId);
      fetchUsers();
      showSuccessToast('User account has been rejected');
    } catch (error) {
      showErrorToast('Failed to reject user account');
    } finally {
      setPendingRejectUserId(null);
    }
  };

  const handleActivateClick = (userId: string) => {
    setPendingActivateUserId(userId);
    setActivateModalOpen(true);
  };

  const handleConfirmActivate = async () => {
    if (!pendingActivateUserId) return;
    setActivateModalOpen(false);
    try {
      await adminService.activateStaff(pendingActivateUserId);
      fetchUsers();
      showSuccessToast('User account has been activated');
    } catch (error) {
      showErrorToast('Failed to activate user account');
    } finally {
      setPendingActivateUserId(null);
    }
  };

  const handleSuspendClick = (userId: string) => {
    setPendingSuspendUserId(userId);
    setSuspendModalOpen(true);
  };

  const handleConfirmSuspend = async () => {
    if (!pendingSuspendUserId) return;
    setSuspendModalOpen(false);
    try {
      await adminService.suspendStaff(pendingSuspendUserId);
      fetchUsers();
      showSuccessToast('User account has been suspended');
    } catch (error) {
      showErrorToast('Failed to suspend user account');
    } finally {
      setPendingSuspendUserId(null);
    }
  };

  const handleGeneratePasswordClick = (userId: string) => {
    setPendingUserId(userId);
    setConfirmPasswordModalOpen(true);
  };

  const handleConfirmPasswordGeneration = async () => {
    if (!pendingUserId) return;
    setConfirmPasswordModalOpen(false);
    try {
      const result = await adminService.generatePassword(pendingUserId, true);
      setNewPassword(result.newPassword);
      setPasswordCopied(false);
      setPasswordModalOpen(true);
    } catch (error) {
    } finally {
      setPendingUserId(null);
    }
  };

  const handleCopyPassword = async () => {
    if (!newPassword) return;
    try {
      await navigator.clipboard.writeText(newPassword);
      setPasswordCopied(true);
      showSuccessToast('Password copied to clipboard!');
      setTimeout(() => setPasswordCopied(false), TOAST_CONFIG.PASSWORD_COPIED_TIMEOUT);
    } catch (error) {
      showErrorToast('Failed to copy password to clipboard');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            Staff Management
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
            Manage employee accounts, departments, and access permissions.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm flex flex-col lg:flex-row gap-4"
      >
        <div className="flex-1 relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-neolab-green transition-colors" />
          <input
            type="text"
            placeholder="Search by name, ID, or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-medium"
          />
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="relative">
            <label htmlFor="dept-filter" className="sr-only">
              Department
            </label>
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              id="dept-filter"
              value={department}
              onChange={(e) => {
                setDepartment(e.target.value || undefined);
                setPage(1);
              }}
              className="pl-10 pr-8 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-bold appearance-none cursor-pointer min-w-[160px]"
            >
              <option value="">All Departments</option>
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <label htmlFor="status-filter" className="sr-only">
              Status
            </label>
            <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              id="status-filter"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value || undefined);
                setPage(1);
              }}
              className="pl-10 pr-8 py-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 focus:ring-neolab-green/20 outline-none text-sm font-bold appearance-none cursor-pointer min-w-[160px]"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="suspended">Suspended</option>
              <option value="deactivated">Deactivated</option>
            </select>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-xl bg-white dark:bg-neolab-forest border border-neolab-green/5 dark:border-white/5 shadow-sm overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                <th className="px-4 py-3 text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                  Employee
                </th>
                <th className="px-4 py-3 text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                  Department
                </th>
                <th className="px-4 py-3 text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                  Role
                </th>
                <th className="px-4 py-3 text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-black text-slate-400 uppercase tracking-[0.2em] text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neolab-green/5 dark:divide-white/5">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <div className="w-8 h-8 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-xs text-slate-400 font-bold">Fetching staff records...</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center">
                    <p className="text-xs text-slate-400 font-bold">
                      No staff members found matching your criteria.
                    </p>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user._id}
                    className="hover:bg-neolab-green/5 dark:hover:bg-white/5 transition-colors group"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-neolab-green/5 dark:bg-white/5 flex items-center justify-center font-black text-xs text-slate-500 group-hover:bg-neolab-green/10 group-hover:text-neolab-green transition-colors">
                          {user.profile.firstName[0]}
                          {user.profile.lastName[0]}
                        </div>
                        <div>
                          <Tooltip content={user.profile.jobTitle || 'No job title'} position="top">
                            <p className="text-xs font-black text-slate-900 dark:text-white cursor-default">
                              {user.profile.firstName} {user.profile.lastName}
                            </p>
                          </Tooltip>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                            ID: {formatEmployeeId(user.employeeId)} • @{user.username}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400 bg-neolab-green/5 dark:bg-white/5 px-2 py-1 rounded-lg">
                        {user.profile.department}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-black text-blue-500 uppercase tracking-widest">
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border',
                          user.accountStatus === 'active' &&
                            'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
                          user.accountStatus === 'pending_approval' &&
                            'bg-amber-500/10 text-amber-600 border-amber-500/20',
                          user.accountStatus === 'suspended' &&
                            'bg-rose-500/10 text-rose-600 border-rose-500/20',
                          user.accountStatus === 'deactivated' &&
                            'bg-slate-500/10 text-slate-600 border-slate-500/20',
                        )}
                      >
                        <div
                          className={cn(
                            'w-1.5 h-1.5 rounded-full',
                            user.accountStatus === 'active' && 'bg-emerald-500',
                            user.accountStatus === 'pending_approval' && 'bg-amber-500',
                            user.accountStatus === 'suspended' && 'bg-rose-500',
                            user.accountStatus === 'deactivated' && 'bg-slate-500',
                          )}
                        />
                        {formatStatus(user.accountStatus)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Tooltip content="View Details" position="top">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedUser(user);
                              setDetailsModalOpen(true);
                            }}
                            className="p-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 text-slate-400 hover:text-neolab-green hover:bg-neolab-green/10 transition-all"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </Tooltip>

                        {!isCurrentSuperAdmin(user) && (
                          <Tooltip content="Manual Entry / Override" position="top">
                            <button
                              type="button"
                              onClick={() => handleOverrideClick(user)}
                              className="p-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 text-slate-400 hover:text-amber-500 hover:bg-amber-500/10 transition-all"
                            >
                              <Clock className="w-5 h-5" />
                            </button>
                          </Tooltip>
                        )}

                        {user.accountStatus === 'pending_approval' && (
                          <>
                            <Tooltip content="Approve" position="top">
                              <button
                                type="button"
                                onClick={() => handleApproveClick(user._id)}
                                className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-all"
                              >
                                <CheckCircle2 className="w-5 h-5" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Reject" position="top">
                              <button
                                type="button"
                                onClick={() => handleRejectClick(user._id)}
                                className="p-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 hover:bg-rose-500 hover:text-white transition-all"
                              >
                                <XCircle className="w-5 h-5" />
                              </button>
                            </Tooltip>
                          </>
                        )}

                        {user.accountStatus === 'active' && !isCurrentSuperAdmin(user) && (
                          <Tooltip content="Suspend" position="top">
                            <button
                              type="button"
                              onClick={() => handleSuspendClick(user._id)}
                              className="p-2 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 hover:bg-rose-500 hover:text-white transition-all"
                            >
                              <ShieldAlert className="w-5 h-5" />
                            </button>
                          </Tooltip>
                        )}

                        {(user.accountStatus === 'suspended' ||
                          user.accountStatus === 'deactivated') && (
                          <Tooltip content="Activate" position="top">
                            <button
                              type="button"
                              onClick={() => handleActivateClick(user._id)}
                              className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 hover:bg-emerald-500 hover:text-white transition-all"
                            >
                              <ShieldCheck className="w-5 h-5" />
                            </button>
                          </Tooltip>
                        )}

                        {!isCurrentSuperAdmin(user) && (
                          <Tooltip content="Reset Password" position="top">
                            <button
                              type="button"
                              onClick={() => handleGeneratePasswordClick(user._id)}
                              className="p-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 text-slate-400 hover:text-blue-500 hover:bg-blue-500/10 transition-all"
                            >
                              <Key className="w-5 h-5" />
                            </button>
                          </Tooltip>
                        )}

                        {currentUser?.role === 'super_admin' && (
                          <Tooltip content="Settings" position="top">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/user-settings/${user.employeeId}`)}
                              className="p-2 rounded-xl bg-neolab-green/5 dark:bg-white/5 text-slate-400 hover:text-neolab-green hover:bg-neolab-green/10 transition-all"
                            >
                              <Settings className="w-5 h-5" />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="px-8 py-6 bg-neolab-green/[0.02] dark:bg-white/5 border-t border-neolab-green/10 dark:border-white/10 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
              Showing{' '}
              <span className="text-slate-900 dark:text-white">
                {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, total)}
              </span>{' '}
              of {total} members
            </p>
            <div className="flex items-center gap-2">
              <Tooltip content="Previous Page" position="top">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50 transition-all hover:shadow-md"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </Tooltip>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.ceil(total / pageSize) }).map((_, i) => (
                  <Tooltip key={i} content={`Page ${i + 1}`} position="top">
                    <button
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={cn(
                        'w-10 h-10 rounded-xl font-black text-sm transition-all',
                        page === i + 1
                          ? 'bg-neolab-green text-white shadow-lg shadow-green-500/20'
                          : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50',
                      )}
                    >
                      {i + 1}
                    </button>
                  </Tooltip>
                ))}
              </div>
              <Tooltip content="Next Page" position="top">
                <button
                  type="button"
                  disabled={page >= Math.ceil(total / pageSize)}
                  onClick={() => setPage(page + 1)}
                  className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 disabled:opacity-50 transition-all hover:shadow-md"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {detailsModalOpen && selectedUser && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => setDetailsModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-3xl bg-white dark:bg-neolab-forest rounded-[3rem] shadow-2xl overflow-hidden border border-white/10"
            >
              <div className="p-10">
                <div className="flex items-center justify-between mb-10">
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] bg-neolab-green/10 flex items-center justify-center text-3xl font-black text-neolab-green">
                      {selectedUser.profile.firstName[0]}
                      {selectedUser.profile.lastName[0]}
                    </div>
                    <div>
                      <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                        {selectedUser.profile.firstName} {selectedUser.profile.lastName}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs font-black text-neolab-green uppercase tracking-widest">
                          {selectedUser.profile.department}
                        </span>
                        <div className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                          ID: {formatEmployeeId(selectedUser.employeeId)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDetailsModalOpen(false)}
                    className="p-3 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-500 hover:text-rose-500 transition-colors"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  <div className="space-y-6">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 dark:border-slate-800 pb-2">
                      Profile Information
                    </h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Username</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          @{selectedUser.username}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Phone Number
                        </p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          {selectedUser.phoneNumber}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Job Title</p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          {selectedUser.profile.jobTitle}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Role</p>
                        <p className="text-sm font-black text-blue-500 uppercase">
                          {selectedUser.role}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 dark:border-slate-800 pb-2">
                      Attendance Config
                    </h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Mon-Fri Clock-In
                        </p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          {selectedUser.attendanceConfig.monToFriClockInTime}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Grace Period
                        </p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          {selectedUser.attendanceConfig.gracePeriodMinutes} mins
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Normal Saturday
                        </p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          {selectedUser.attendanceConfig.normalSatClockInTime}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Last Saturday
                        </p>
                        <p className="text-sm font-black text-slate-700 dark:text-slate-200">
                          {selectedUser.attendanceConfig.lastSatOfMonthClockInTime}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Late Arrival Fee
                        </p>
                        <p className="text-sm font-black text-rose-500">
                          {formatNaira(selectedUser.attendanceConfig.lateArrivalFee)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">
                          Missed Clock-Out
                        </p>
                        <p className="text-sm font-black text-rose-500">
                          {formatNaira(selectedUser.attendanceConfig.missedClockOutFee)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setDetailsModalOpen(false)}
                    className="px-8 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm"
                  >
                    Close
                  </button>
                  {!isCurrentSuperAdmin(selectedUser) && (
                    <button
                      type="button"
                      onClick={() => {
                        handleGeneratePasswordClick(selectedUser._id);
                        setDetailsModalOpen(false);
                      }}
                      className="px-8 py-4 rounded-2xl bg-neolab-green text-white font-black text-sm shadow-lg shadow-green-500/20"
                    >
                      Reset Password
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmPasswordModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setConfirmPasswordModalOpen(false);
                setPendingUserId(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <ShieldAlert className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                  Confirm Password Reset
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  This action will change the user's password and cannot be undone. The user will be
                  required to change their password on their next login.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 mb-8">
                <p className="text-xs font-bold text-amber-700 dark:text-amber-300 leading-relaxed">
                  <strong className="font-black">Warning:</strong> The current password will be
                  permanently replaced. Make sure you securely share the new password with the staff
                  member.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmPasswordModalOpen(false);
                    setPendingUserId(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPasswordGeneration}
                  className="flex-1 px-6 py-4 rounded-2xl bg-amber-500 text-white font-black text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-600 transition-all"
                >
                  Confirm Reset
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {suspendModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setSuspendModalOpen(false);
                setPendingSuspendUserId(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-rose-500/10 flex items-center justify-center">
                  <ShieldAlert className="w-8 h-8 text-rose-500" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                  Suspend User Account
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  Are you sure you want to suspend this user's account? The user will not be able to
                  access the system until the account is reactivated.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 mb-8">
                <p className="text-xs font-bold text-rose-700 dark:text-rose-300 leading-relaxed">
                  <strong className="font-black">Warning:</strong> Suspending an account will
                  immediately revoke the user's access. This action can be reversed by activating
                  the account later.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setSuspendModalOpen(false);
                    setPendingSuspendUserId(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSuspend}
                  className="flex-1 px-6 py-4 rounded-2xl bg-rose-500 text-white font-black text-sm shadow-lg shadow-rose-500/20 hover:bg-rose-600 transition-all"
                >
                  Suspend Account
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {overrideModalOpen && pendingOverrideUser && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setOverrideModalOpen(false);
                setPendingOverrideUser(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                  Manual Entry / Override
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  Override functionality requires an attendance record context. Please navigate to
                  the Attendance Management page to override attendance records for this user.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 mb-8">
                <p className="text-xs font-bold text-amber-700 dark:text-amber-300 leading-relaxed">
                  <strong className="font-black">Note:</strong> To override attendance records, you
                  need to access the user's attendance history from the Attendance Management
                  section.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setOverrideModalOpen(false);
                    setPendingOverrideUser(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOverrideModalOpen(false);
                    setPendingOverrideUser(null);
                    navigate('/admin/attendance');
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-amber-500 text-white font-black text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-600 transition-all"
                >
                  Go to Attendance
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {approveModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setApproveModalOpen(false);
                setPendingApproveUserId(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                  Approve User Account
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  Are you sure you want to approve this user's account? The user will be able to
                  access the system and start using their account immediately.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 mb-8">
                <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300 leading-relaxed">
                  <strong className="font-black">Note:</strong> Approving an account will activate
                  it and grant the user full access to the system based on their assigned role.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setApproveModalOpen(false);
                    setPendingApproveUserId(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmApprove}
                  className="flex-1 px-6 py-4 rounded-2xl bg-emerald-500 text-white font-black text-sm shadow-lg shadow-emerald-500/20 hover:bg-emerald-600 transition-all"
                >
                  Approve Account
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {rejectModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setRejectModalOpen(false);
                setPendingRejectUserId(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-rose-500/10 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-rose-500" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                  Reject User Account
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  Are you sure you want to reject this user's account? This action will permanently
                  deny access and the user will not be able to use the system.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 mb-8">
                <p className="text-xs font-bold text-rose-700 dark:text-rose-300 leading-relaxed">
                  <strong className="font-black">Warning:</strong> Rejecting an account is a
                  permanent action. The user will not be able to access the system. This action
                  cannot be easily undone.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setRejectModalOpen(false);
                    setPendingRejectUserId(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmReject}
                  className="flex-1 px-6 py-4 rounded-2xl bg-rose-500 text-white font-black text-sm shadow-lg shadow-rose-500/20 hover:bg-rose-600 transition-all"
                >
                  Reject Account
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activateModalOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/80 backdrop-blur-md"
              onClick={() => {
                setActivateModalOpen(false);
                setPendingActivateUserId(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3rem] p-10 shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <ShieldCheck className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                  Activate User Account
                </h3>
                <p className="text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                  Are you sure you want to activate this user's account? The user will regain access
                  to the system and be able to use their account immediately.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 mb-8">
                <p className="text-xs font-bold text-emerald-700 dark:text-emerald-300 leading-relaxed">
                  <strong className="font-black">Note:</strong> Activating an account will restore
                  the user's access to the system. The account will be set to active status and the
                  user can log in and use all features based on their assigned role.
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setActivateModalOpen(false);
                    setPendingActivateUserId(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl bg-neolab-green/5 dark:bg-white/5 text-slate-600 dark:text-slate-300 font-black text-sm hover:bg-neolab-green/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmActivate}
                  className="flex-1 px-6 py-4 rounded-2xl bg-emerald-500 text-white font-black text-sm shadow-lg shadow-emerald-500/20 hover:bg-emerald-600 transition-all"
                >
                  Activate Account
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {passwordModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neolab-deep-green/90 backdrop-blur-xl"
              onClick={() => setPasswordModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 40 }}
              className="relative z-10 w-full max-w-md bg-white dark:bg-neolab-forest rounded-[3.5rem] p-10 text-center shadow-2xl border border-white/5"
            >
              <div className="relative w-20 h-20 mx-auto mb-8">
                <div className="relative w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center shadow-xl shadow-blue-500/40">
                  <Key className="w-10 h-10 text-white" />
                </div>
              </div>

              <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">
                Password Generated
              </h3>
              <p className="text-slate-500 dark:text-slate-400 font-medium mb-8">
                A new temporary password has been created for the user.
              </p>

              <div className="relative p-6 rounded-[2rem] bg-neolab-green/5 dark:bg-white/5 border-2 border-dashed border-neolab-green/10 dark:border-white/10 mb-8 select-all">
                <p className="text-3xl font-black text-slate-900 dark:text-white tracking-widest font-mono pr-12">
                  {newPassword}
                </p>
                <button
                  type="button"
                  onClick={handleCopyPassword}
                  className="absolute top-4 right-4 p-2 rounded-xl bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-neolab-green/10 hover:text-neolab-green transition-all"
                  title="Copy password"
                >
                  {passwordCopied ? (
                    <CopyCheck className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>

              <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-800 mb-8">
                <p className="text-xs font-bold text-rose-600 dark:text-rose-400 leading-relaxed">
                  Please share this password securely. The user will be required to change it on
                  their next login.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPasswordModalOpen(false)}
                className="w-full py-5 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black text-lg shadow-xl"
              >
                Done
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default StaffManagement;
